import { createTheme } from '@mui/material/styles';
import {
  CheckBoxIcon,
  CheckBoxOutlineBlankIcon,
  IndeterminateCheckBoxIcon,
} from 'fbm-ui';

declare module '@mui/material/styles/createPalette' {
  export interface Palette {
    default: PaletteColor;
  }

  export interface PaletteOptions {
    default?: PaletteColorOptions;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    link: true;
  }
  interface ButtonPropsColorOverrides {
    default: true;
  }
}

const theme = createTheme({
  components: {
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontSize: '14px',
          lineHeight: '22px',
          '&.MuiButton-outlinedDefault': {
            borderColor: 'rgba(0, 0, 0, 0.12)',
          },
          textTransform: 'none',
        },
        contained: {
          border: '1px solid transparent',
          '&.Mui-disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            border: '1px solid rgba(0, 0, 0, 0.04)',
          },
        },
        outlined: {
          fontWeight: 400,
          backgroundColor: 'white',
        },
        sizeSmall: {
          padding: '0px 8px',
        },
        sizeMedium: {
          padding: '4px 16px',
        },
        sizeLarge: {
          padding: '8px 24px',
        },
        text: {
          color: 'rgba(0, 0, 0, 0.56)',
          border: '1px solid transparent',
        },
      },
      variants: [
        {
          props: { variant: 'link' },
          style: {
            color: '#576B95',
            border: '1px solid transparent',
          },
        },
      ],
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          input: {
            padding: '12.5px 12px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,0.26)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4caf50',
            borderWidth: '1px',
          },
          '&.Mui-error:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline':
            {
              borderColor: '#f44336',
            },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
        notchedOutline: {
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgba(0,0,0,0.26)',
          top: '-4px',
          '&.Mui-focused': {
            top: '0px',
          },
          '&.MuiFormLabel-filled': {
            top: '0px',
          },
        },
        sizeSmall: {
          top: '-2px',
        },
      },
    },
    // MuiSelect: {
    //   styleOverrides: {
    //     select: {
    //       fontSize: '14px',
    //       lineHeight: '22px',
    //       padding: '7px 12px',
    //     },
    //   },
    // },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          lineHeight: '1.2rem',
          background: '#F9FAF9',
          fontWeight: 400,
        },
        arrow: {
          color: 'rgba(0,0,0,0.8)',
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        indeterminateIcon: <IndeterminateCheckBoxIcon />,
        checkedIcon: <CheckBoxOutlineBlankIcon />,
        icon: <CheckBoxIcon />,
        color: 'primary',
      },
    },
  },
  palette: {
    primary: {
      main: '#4caf50',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ff6c6c',
      contrastText: '#ffffff',
    },
    info: {
      main: '#576B95',
      contrastText: '#ffffff',
    },
    default: {
      // main: '#616161',
      main: 'rgba(0, 0, 0, 0.86)',
      contrastText: '#ffffff',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.86)',
      secondary: 'rgba(0, 0, 0, 0.56)',
      disabled: 'rgba(0, 0, 0, 0.26)',
    },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: {
    fontFamily: [
      'PingFang SC',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h4: {
      fontSize: '34px',
      lineHeight: '48px',
    },
    h5: {
      fontSize: '24px',
      lineHeight: '32px',
    },
    h6: {
      fontSize: '20px',
      lineHeight: '28px',
    },
    body1: {
      fontSize: '16px',
      lineHeight: '24px',
    },
    body2: {
      fontSize: '14px',
      lineHeight: '22px',
    },
    caption: {
      fontSize: '12px',
      lineHeight: '18px',
    },
  },
  shadows: [
    'none',
    '0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
    '0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
    '0px 1px 8px 0px rgba(0, 0, 0, 0.2),0px 3px 4px 0px rgba(0, 0, 0, 0.14),0px 3px 3px -2px rgba(0, 0, 0, 0.12)',
    '0px 4px 8px 0px rgba(0, 0, 0, 0.1)',
    '0px 3px 5px -1px rgba(0, 0, 0, 0.2),0px 5px 8px 0px rgba(0, 0, 0, 0.14),0px 1px 14px 0px rgba(0, 0, 0, 0.12)',
    '0px 3px 5px -1px rgba(0, 0, 0, 0.2),0px 6px 10px 0px rgba(0, 0, 0, 0.14),0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
    '0px 4px 5px -2px rgba(0, 0, 0, 0.2),0px 7px 10px 1px rgba(0, 0, 0, 0.14),0px 2px 16px 1px rgba(0, 0, 0, 0.12)',
    '0px 3px 6px -4px rgba(0, 0, 0, 0.12),0px 6px 16px 0px rgba(0, 0, 0, 0.08),0px 9px 28px 8px rgba(0, 0, 0, 0.05)',
    '0px 5px 6px -3px rgba(0, 0, 0, 0.2),0px 9px 12px 1px rgba(0, 0, 0, 0.14),0px 3px 16px 2px rgba(0, 0, 0, 0.12)',
    '0px 6px 6px -3px rgba(0, 0, 0, 0.2),0px 10px 14px 1px rgba(0, 0, 0, 0.14),0px 4px 18px 3px rgba(0, 0, 0, 0.12)',
    '0px 6px 7px -4px rgba(0, 0, 0, 0.2),0px 11px 15px 1px rgba(0, 0, 0, 0.14),0px 4px 20px 3px rgba(0, 0, 0, 0.12)',
    '0px 7px 8px -4px rgba(0, 0, 0, 0.2),0px 12px 17px 2px rgba(0, 0, 0, 0.14),0px 5px 22px 4px rgba(0, 0, 0, 0.12)',
    '0px 7px 8px -4px rgba(0, 0, 0, 0.2),0px 13px 19px 2px rgba(0, 0, 0, 0.14),0px 5px 24px 4px rgba(0, 0, 0, 0.12)',
    '0px 7px 9px -4px rgba(0, 0, 0, 0.2),0px 14px 21px 2px rgba(0, 0, 0, 0.14),0px 5px 26px 4px rgba(0, 0, 0, 0.12)',
    '0px 8px 9px -5px rgba(0, 0, 0, 0.2),0px 15px 22px 2px rgba(0, 0, 0, 0.14),0px 6px 28px 5px rgba(0, 0, 0, 0.12)',
    '0px 8px 10px -5px rgba(0, 0, 0, 0.2),0px 16px 24px 2px rgba(0, 0, 0, 0.14),0px 6px 30px 5px rgba(0, 0, 0, 0.12)',
    '0px 8px 11px -5px rgba(0, 0, 0, 0.2),0px 17px 26px 2px rgba(0, 0, 0, 0.14),0px 6px 32px 5px rgba(0, 0, 0, 0.12)',
    '0px 9px 11px -5px rgba(0, 0, 0, 0.2),0px 18px 28px 2px rgba(0, 0, 0, 0.14),0px 7px 34px 6px rgba(0, 0, 0, 0.12)',
    '0px 9px 12px -6px rgba(0, 0, 0, 0.2),0px 19px 29px 2px rgba(0, 0, 0, 0.14),0px 7px 36px 6px rgba(0, 0, 0, 0.12)',
    '0px 10px 13px -6px rgba(0, 0, 0, 0.2),0px 20px 31px 3px rgba(0, 0, 0, 0.14),0px 8px 38px 7px rgba(0, 0, 0, 0.12)',
    '0px 10px 13px -6px rgba(0, 0, 0, 0.2),0px 21px 33px 3px rgba(0, 0, 0, 0.14),0px 8px 40px 7px rgba(0, 0, 0, 0.12)',
    '0px 10px 14px -6px rgba(0, 0, 0, 0.2),0px 22px 35px 3px rgba(0, 0, 0, 0.14),0px 8px 42px 7px rgba(0, 0, 0, 0.12)',
    '0px 11px 14px -7px rgba(0, 0, 0, 0.2),0px 23px 36px 3px rgba(0, 0, 0, 0.14),0px 9px 44px 8px rgba(0, 0, 0, 0.12)',
    '0px 11px 15px -7px rgba(0, 0, 0, 0.2),0px 24px 38px 3px rgba(0, 0, 0, 0.14),0px 9px 46px 8px rgba(0, 0, 0, 0.12)',
  ],
});

export default theme;
